import React, { Component, ReactNode } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { PushNotification } from '../../../../domain/notification/NotificationDomains';
import CollectionNames from '../../../../constant/CollectionNames';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import NotificationHeaderComponent from './NotificationHeaderComponent';
import NotificationBodyComponent from './NotificationBodyComponent';
import { RootState } from '../../../../redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { AuthState } from '../../../../redux/reducers/AuthReducer';
import CustomDismissible from '../../CustomDismissible';
import Icon from '../../../../icons/Icon';
import { CheckerRequest } from '../../../../domain/maker-checker/MakerCheckerDomains';
import MakerCheckerClient from '../../../../client/maker-checker/MakerCheckerClient';
import GatewayContainer from '../../../../config/GatewayContainer';
import GatewayNames from '../../../../constant/GatewayNames';
import { createPortal } from 'react-dom';
import QueryBuilder = query.QueryBuilder;
import and = query.and;
import or = query.or;
import eq = query.eq;
import inside = query.inside;
import CustomBadge from '../../CustomBadge';
import ValueUtil from '../../../../util/ValueUtil';

class NotificationContainerComponent extends Component<Props, State> {
  public state: State = new State();

  private notificationSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly makerCheckerClient: MakerCheckerClient = GatewayContainer.getInstance().get(GatewayNames.MAKER_CHECKER_CLIENT);

  public render(): ReactNode {
    const { notifications, unreadCount, checkerRequests } = this.state;
    const shortened = ValueUtil.numberShorten(unreadCount, 0);
    return (
      <CustomDismissible id='notification-container' onDismiss={() => this.setState({ isOpen: false })}>
        <div
          id='notification-container'
          className='bg-transparent bg-[#FFFFFF29] cursor-pointer h-8 w-8 rounded-full flex justify-center items-center text-center text-white relative'
        >
          <span onClick={() => this.setState({ isOpen: !this.state.isOpen })} className={'flex items-center justify-center h-8 w-8 cursor-pointer'}>
            <Icon icon={'notifications_none'} className={`${unreadCount > 0 ? 'text-red-300' : ''}`} size={22} />
            {unreadCount > 0 && <CustomBadge value={shortened} color='#458EF7' style={{ width: 20, height: 20, position: 'absolute', left: 20, top: -5 }} />}
          </span>
          <div className={`${this.state.isOpen ? 'flex' : 'hidden'}`}>
            {createPortal(
              <div className={`top-0 left-0 h-full w-full bg-[#111111] z-[1001] opacity-30 ${this.state.isOpen ? 'fixed' : 'hidden'}`}></div>,
              document.body,
            )}
            <div
              style={{ boxShadow: '0px 4px 6px -2px #00000008,0px 12px 16px -4px #00000014' }}
              className='absolute w-[440px] right-0 top-8 !z-[1002] rounded-[12px]'
            >
              <div className='w-full flex flex-col max-h-[450px] overflow-scroll shrink-0 bg-white  border-[#E0E4E8] border-[1px] rounded-[12px]'>
                <NotificationHeaderComponent />
                <NotificationBodyComponent notifications={notifications} checkerRequests={checkerRequests} />
              </div>
            </div>
          </div>
        </div>
      </CustomDismissible>
    );
  }

  public componentDidMount(): void {
    this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = () => {
    const notificationQuery = this.getQuery();
    this.notificationSubscription = subscribe.Formica.subscribe(CollectionNames.NOTIFICATIONS, notificationQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.notificationSubscription]);
  };

  private tracker = async () => {
    if (this.notificationSubscription?.ready()) {
      await this.buildData();
    }
  };

  private stopTracker = async () => {
    await this.notificationSubscription?.stop();
    this.computation?.stop();
  };

  private buildData = async () => {
    const { rows } = this.state;
    const notificationQuery = QueryBuilder.select().from(CollectionNames.NOTIFICATIONS).limit(rows).orderBy({ field: 'timestamp', operator: 'DESC' }).build();
    const notifications = await this.repository.findMany<PushNotification>(notificationQuery);
    const unreadCount = notifications.filter((n) => !n.read).length;
    const checkerRequests = await this.makerCheckerClient.findMany(notifications.flatMap((n) => n.metadata.id));
    this.setState({ notifications, unreadCount, checkerRequests });
  };

  private getQuery = () => {
    const { rows, currentPage } = this.state;
    const { user } = this.props;
    return QueryBuilder.select()
      .from(CollectionNames.NOTIFICATIONS)
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .orderBy({ field: 'timestamp', operator: 'DESC' })
      .where(
        or(
          eq('target.type', 'ALL'),
          and(eq('target.type', 'USER'), eq('target.value', user.user?.username)),
          and(eq('target.type', 'GROUP'), inside('target.value', user.user?.groups || [])),
        ),
      )
      .build();
  };
}

interface Props extends ReduxProps {}

class State {
  notifications: PushNotification[] = [];
  checkerRequests: CheckerRequest[] = [];
  rows: number = 1000;
  totalRecords: number = 0;
  currentPage: number = 1;
  isOpen: boolean = false;
  unreadCount: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth as AuthState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(NotificationContainerComponent);
